import React, { useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps, StepHazardData, StepHazardItem, ECMDriver, ECM_LABELS, HierarchyControls } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { EditItemModal } from '../ui/EditItemModal';
interface StepHazardScreenProps extends ScreenProps {
  stepIndex: number;
  stepData: StepHazardData;
  conditions: string[];
  onUpdateStep: (stepIndex: number, data: StepHazardData) => void;
}
export function StepHazardScreen({
  stepIndex,
  stepData,
  conditions,
  onUpdateStep,
  onNext
}: StepHazardScreenProps) {
  const [editingField, setEditingField] = useState<{
    hazardId: string;
    field: 'eliminateSubstitute' | 'engineering' | 'adminTraining' | 'ppe';
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showAddEquipment, setShowAddEquipment] = useState<string | null>(null);
  const [newEquipment, setNewEquipment] = useState('');
  const handleUpdateControl = (id: string, field: keyof HierarchyControls, value: string) => {
    const newHazards = stepData.hazards.map(h => h.id === id ? {
      ...h,
      controls: {
        ...h.controls,
        [field]: value
      }
    } : h);
    onUpdateStep(stepIndex, {
      ...stepData,
      hazards: newHazards
    });
  };
  const handleCycleECM = (hazard: StepHazardItem) => {
    if (!hazard.relevant) return;
    const cycle: Record<ECMDriver, ECMDriver> = {
      E: 'C',
      C: 'M',
      M: 'E'
    };
    const newHazards = stepData.hazards.map(h => h.id === hazard.id ? {
      ...h,
      ecmDriver: cycle[hazard.ecmDriver]
    } : h);
    onUpdateStep(stepIndex, {
      ...stepData,
      hazards: newHazards
    });
  };
  const handleAddEquipment = (hazardId: string) => {
    const trimmed = newEquipment.trim();
    if (trimmed) {
      const newHazards = stepData.hazards.map(h => h.id === hazardId && !h.equipment.includes(trimmed) ? {
        ...h,
        equipment: [...h.equipment, trimmed]
      } : h);
      onUpdateStep(stepIndex, {
        ...stepData,
        hazards: newHazards
      });
    }
    setNewEquipment('');
    setShowAddEquipment(null);
  };
  const handleRemoveEquipment = (hazardId: string, eqIndex: number) => {
    const newHazards = stepData.hazards.map(h => h.id === hazardId ? {
      ...h,
      equipment: h.equipment.filter((_, i) => i !== eqIndex)
    } : h);
    onUpdateStep(stepIndex, {
      ...stepData,
      hazards: newHazards
    });
  };
  const startEdit = (hazardId: string, field: 'eliminateSubstitute' | 'engineering' | 'adminTraining' | 'ppe', currentValue: string) => {
    setEditingField({
      hazardId,
      field
    });
    setEditValue(currentValue);
  };
  const saveEdit = (newText: string) => {
    if (!editingField) return;
    const {
      hazardId,
      field
    } = editingField;
    handleUpdateControl(hazardId, field, newText);
    setEditingField(null);
  };
  const getECMStyle = (driver: ECMDriver) => {
    switch (driver) {
      case 'E':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'C':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'M':
        return 'bg-amber-50 text-amber-600 border-amber-200';
    }
  };
  const controlFields: {
    key: keyof HierarchyControls;
    label: string;
  }[] = [{
    key: 'eliminateSubstitute',
    label: 'Elim/Sub'
  }, {
    key: 'engineering',
    label: 'Engineering'
  }, {
    key: 'adminTraining',
    label: 'Admin/Training'
  }, {
    key: 'ppe',
    label: 'PPE'
  }];
  const getModalTitle = (field: keyof HierarchyControls) => {
    switch (field) {
      case 'eliminateSubstitute':
        return 'Edit Elimination / Substitution';
      case 'engineering':
        return 'Edit Engineering Control';
      case 'adminTraining':
        return 'Edit Admin / Training Control';
      case 'ppe':
        return 'Edit PPE';
    }
  };
  // Only show relevant hazards on the controls screen
  const relevantHazards = stepData.hazards.filter(h => h.relevant);
  return <div className="flex flex-col h-full" data-id="element-1530">
      <div className="mb-3" data-id="element-1531">
        <p className="text-sm font-bold text-gray-400 mb-0.5" data-id="element-1532">
          Step {stepIndex + 1} — Controls
        </p>
        <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-1533">
          {stepData.description}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto pb-20 -mx-1 px-1 space-y-3" data-id="element-1534">
        {relevantHazards.length === 0 && <div className="text-center py-8" data-id="element-1535">
            <p className="text-gray-400 text-sm" data-id="element-1536">
              No relevant hazards for this step.
            </p>
            <p className="text-gray-400 text-xs mt-1" data-id="element-1537">
              All hazards were marked as not relevant in the review.
            </p>
          </div>}
        {relevantHazards.map((hazard, idx) => <motion.div key={hazard.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.05
      }} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-1538">
            {/* Header row — read-only */}
            <div className="p-3 pb-2" data-id="element-1539">
              <h5 className="font-bold text-sm leading-snug text-gray-900" data-id="element-1540">
                {hazard.hazard}
              </h5>
              <p className="text-xs mt-0.5 text-gray-500" data-id="element-1541">{hazard.source}</p>
            </div>

            {/* ECM Badge + Controls */}
            <div className="px-3 pb-3 space-y-2" data-id="element-1542">
              <button onClick={() => handleCycleECM(hazard)} className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider transition-colors ${getECMStyle(hazard.ecmDriver)}`} data-id="element-1543">
                {hazard.ecmDriver} — {ECM_LABELS[hazard.ecmDriver]}
              </button>

              {/* Controls — tap to edit via modal */}
              <div className="space-y-1.5 bg-gray-50 rounded-lg p-2.5 border border-gray-200" data-id="element-1544">
                {controlFields.map(({
              key,
              label
            }) => <div key={key} data-id="element-1545">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1546">
                      {label}
                    </span>
                    <p onClick={() => startEdit(hazard.id, key, hazard.controls[key])} className="text-xs text-gray-700 leading-snug mt-0.5 cursor-pointer" data-id="element-1547">
                      {hazard.controls[key]}
                    </p>
                  </div>)}
              </div>

              {/* Equipment — editable */}
              <div data-id="element-1548">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-1549">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1550">
                    Tools & Equipment
                  </span>
                  <button onClick={() => setShowAddEquipment(hazard.id)} className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-1551">
                    <PlusIcon className="w-2.5 h-2.5" data-id="element-1552" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1" data-id="element-1553">
                  <AnimatePresence data-id="element-1554">
                    {showAddEquipment === hazard.id && <motion.div initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} className="flex items-center gap-1 bg-gray-50 border border-yellow-400 rounded-full pl-2 pr-1 py-0.5" data-id="element-1555">
                        <input autoFocus value={newEquipment} onChange={e => setNewEquipment(e.target.value)} onBlur={() => handleAddEquipment(hazard.id)} onKeyDown={e => {
                    if (e.key === 'Enter') handleAddEquipment(hazard.id);
                    if (e.key === 'Escape') {
                      setShowAddEquipment(null);
                      setNewEquipment('');
                    }
                  }} placeholder="Add..." className="w-20 bg-transparent text-gray-900 text-[11px] outline-none" data-id="element-1556" />
                      </motion.div>}
                  </AnimatePresence>
                  {hazard.equipment.map((eq, i) => <span key={i} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 pl-2 pr-1 py-0.5 rounded-full text-[11px] text-gray-700" data-id="element-1557">
                      {eq}
                      <button onClick={() => handleRemoveEquipment(hazard.id, i)} className="p-0.5 text-gray-400 hover:text-red-500 rounded-full" data-id="element-1558">
                        <XIcon className="w-2.5 h-2.5" data-id="element-1559" />
                      </button>
                    </span>)}
                  {hazard.equipment.length === 0 && showAddEquipment !== hazard.id && <span className="text-gray-400 text-[11px] italic" data-id="element-1560">
                        None
                      </span>}
                </div>
              </div>
            </div>
          </motion.div>)}
      </div>

      <div className="absolute bottom-0 left-0 right-0 pt-3 pb-0 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent" data-id="element-1561">
        <PrimaryButton onClick={onNext} data-id="element-1562">Continue</PrimaryButton>
      </div>

      {/* Edit Control Modal */}
      <EditItemModal isOpen={!!editingField} initialText={editValue} title={editingField ? getModalTitle(editingField.field) : 'Edit Control'} onSave={saveEdit} onCancel={() => setEditingField(null)} data-id="element-1563" />
    </div>;
}